/**
 * The sync pill's copy, as a pure function of `SyncStatus`.
 *
 * Split out of the component so it can be unit-tested in the node environment
 * the rest of the suite runs in (tests/unit/ui-shell.test.ts) — the pill is the
 * one piece of chrome that is always on screen (brief §2), and "Offline · 2
 * queued" being wrong is how a surveyor decides work has been delivered when it
 * has not.
 *
 * Precedence is deliberate and is the whole content of this file:
 *
 *   1. OFFLINE first, even mid-drain. "No signal" is the fact that changes what
 *      the surveyor does next; it outranks a request still unwinding.
 *   2. NOT LINKED. A device with no surveyor token fails every upload, so
 *      "Sync error" would be technically true and useless — the surveyor needs
 *      to be told to open the link from the text message, not to try again.
 *   3. NEEDS ATTENTION. Something is parked and will NOT clear on its own — the
 *      only state in this list that requires a hand. It outranks the transient
 *      error below it for exactly that reason: a retryable failure resolves
 *      itself on the next pass, and this one waits forever if nobody is told.
 *   4. ERROR next. The link is up and the server said no — that is worth an
 *      alarm colour, and the queue is still intact behind it.
 *   5. SYNCING while a drain is running.
 *   6. QUEUED when work is waiting for the next pass.
 *   7. SYNCED only when the outbox is empty, online, and nothing failed. This
 *      badge is a promise; it is never shown while anything is outstanding.
 *
 * ── "NEEDS ATTENTION" IS NOT AN ALARM (D-015) ────────────────────────────────
 *
 * It has its own tone rather than reusing `error`, and the tone is amber, not
 * red. Red is for something going wrong; this is a request for one small
 * decision about one photo while the survey itself is perfectly safe. Every
 * sentence in this state says so before it says anything else, because the
 * audience is a 60-year-old in a dim machine room who has been trained by three
 * decades of software to read red as "you have broken something".
 *
 * It is also the reason the state exists at all. A permanent refusal used to
 * land in `lastError` and pin the pill red forever with no way to clear it — and
 * an alarm nobody can silence is an alarm everybody learns to ignore, which is
 * how the NEXT failure, the real one, goes unnoticed.
 */

import type { SyncStatus } from '../../db/sync';
import { hrefFor } from '../routing';

export type SyncTone = 'idle' | 'busy' | 'ok' | 'error' | 'attention';

export interface SyncPillView {
  tone: SyncTone;
  /** Short label rendered in the pill. Uppercased by CSS. */
  label: string;
  /** Full sentence for the accessible name and the title attribute. */
  detail: string;
  /** Whether the dot pulses — reserved for "something is outstanding". */
  pulse: boolean;
  /**
   * Where the pill GOES — present only when there is somewhere worth going.
   *
   * ── Chip, 2026-08-18 ────────────────────────────────────────────────────
   *   *"An error at the top of the app said 'X items need you', but it gave no
   *    clarification about what those items were, how to fix the issue, or
   *    what to do. That flag at the top is causing confusion."*
   *
   * The pill was a badge. It counted the parked rows and its one sentence —
   * which already carried the first row's plain reason — lived in a `title`
   * attribute, which a phone never shows. So the count was all a surveyor got,
   * and a count with no way in is a puzzle. In the "needs you" state the pill
   * now carries the way to the list of exactly those items. In every other
   * state it stays a badge: a link to an empty screen is a control that does
   * nothing, which is worse than no control.
   */
  href?: string;
}

function plural(count: number, word: string): string {
  return `${count} ${word}${count === 1 ? '' : 's'}`;
}

/**
 * "2 queued (1 photo)" — the photo share matters because photos drain last.
 *
 * Parked rows are subtracted: they are NOT waiting to upload, they are waiting
 * for a person, and counting them here would make "3 items waiting to upload"
 * a sentence that never comes true no matter how long the surveyor stands in
 * the parking lot with four bars.
 */
export function queueDetail(status: SyncStatus): string {
  const sending = Math.max(0, status.queued - status.blocked);
  if (sending === 0) return 'nothing waiting to upload';
  const photos =
    status.queuedPhotos > 0 ? ` (${plural(Math.min(status.queuedPhotos, sending), 'photo')})` : '';
  return `${plural(sending, 'item')} waiting to upload${photos}`;
}

/** "1 photo needs you" — never a count of failures, always a count of decisions. */
function attentionLabel(status: SyncStatus): string {
  const photos = status.attention.filter((entry) => entry.kind === 'photo').length;
  if (photos === status.blocked) return plural(photos, 'photo');
  return plural(status.blocked, 'item');
}

export interface SyncPillContext {
  /** Whether this device holds a surveyor token at all. Defaults to true. */
  linked?: boolean;
}

export function syncPillView(status: SyncStatus, context: SyncPillContext = {}): SyncPillView {
  const queued = status.queued;
  const suffix = queued > 0 ? ` · ${queued} queued` : '';

  if (!status.online) {
    return {
      tone: 'idle',
      label: `Offline${suffix}`,
      detail: `Offline. Everything is saved on this device — ${queueDetail(status)}.`,
      pulse: queued > 0,
    };
  }

  if (context.linked === false) {
    return {
      tone: 'error',
      label: `Not linked${suffix}`,
      // D-019: no longer "open the link Chip or Tim texted you". SMS has been
      // parked since the A2P denial (D-010), so that pointed at a channel that
      // no longer exists. The way in is a setup code taken inside this app.
      detail:
        'This device is not linked to a surveyor yet — ask the office for a setup code and ' +
        `set it up from My Surveys. Nothing is lost in the meantime: ${queueDetail(status)}.`,
      pulse: true,
    };
  }

  if (status.blocked > 0) {
    const what = attentionLabel(status);
    // The first clause is reassurance, the second is the ask, and the ask names
    // exactly one action. Nothing here says "error", "failed" or "invalid".
    const first = status.attention[0];
    // Anything NOT parked is still on its way, and saying so is what keeps this
    // state from reading as "sync has stopped". It has not; one item stepped out
    // of the queue and the rest is moving.
    const rest = status.queued - status.blocked > 0 ? ` The rest is on its way — ${queueDetail(status)}.` : '';
    return {
      tone: 'attention',
      label: `${what} needs you`,
      href: hrefFor({ name: 'attention' }),
      detail:
        `Everything is saved on this phone — nothing is lost. ${what} could not be sent ` +
        `to the office and will wait here until you decide.` +
        (first ? ` ${first.reason}` : '') +
        rest,
      // Steady, not blinking. It is a note, not an emergency.
      pulse: false,
    };
  }

  if (status.lastError !== null && !status.syncing) {
    return {
      tone: 'error',
      label: `Sync error${suffix}`,
      // The same way in as "needs you" (2026-08-18: *"it still says sync error
      // 9 queued"* — a red badge with its one sentence in a title attribute no
      // phone shows). The Needs-you screen prints this sentence in full and
      // says the queue is still moving on its own.
      href: hrefFor({ name: 'attention' }),
      detail: `Upload failed and will retry: ${status.lastError}. Nothing is lost — ${queueDetail(status)}.`,
      pulse: true,
    };
  }

  if (status.syncing) {
    return {
      tone: 'busy',
      label: queued > 0 ? `Syncing · ${queued}` : 'Syncing',
      detail: `Uploading now — ${queueDetail(status)}.`,
      pulse: true,
    };
  }

  if (queued > 0) {
    return {
      tone: 'busy',
      label: `Queued · ${queued}`,
      detail: `Saved on this device — ${queueDetail(status)}.`,
      pulse: true,
    };
  }

  return {
    tone: 'ok',
    label: 'Synced',
    detail: 'Everything on this device has reached the office.',
    pulse: false,
  };
}
